/**
 * Owner: Masa Bayu
 * File: demo-pwa-store.ts
 * Date:Time: 2026-09-16 18:41
 * Description: Demo PWA install · shell SW · notification prefs for T8-E1 UI.
 */

import {
  createPwaInstallRecord,
  createQxreadAccount,
  createQxreadSession,
  projectPwaInstallSurface,
  registerPwaShellWorker,
  transitionPwaInstallState,
  upsertNotificationPreference,
  type QxreadAccount,
  type QxreadNotificationPreference,
  type QxreadPwaInstallProjection,
  type QxreadPwaInstallRecord,
  type QxreadSession,
} from '@qx/qxread-core';

const DEMO_ACCOUNT_ID = 'qxrdacc_demo_pwa';
const DEMO_NOW = '2026-09-16T10:41:00.000Z';

let demoAccount: QxreadAccount = createQxreadAccount({
  accountId: DEMO_ACCOUNT_ID,
  displayName: 'Demo PWA User',
  roles: ['reader'],
});
let demoSession: QxreadSession = createQxreadSession({
  state: 'authenticated',
  accountId: DEMO_ACCOUNT_ID,
  issuedAt: DEMO_NOW,
});
let demoInstall: QxreadPwaInstallRecord | null = null;
let demoPrefs: QxreadNotificationPreference[] = [];
let demoProjection: QxreadPwaInstallProjection | null = null;
let lastStatus = 'No PWA install record yet.';

function ensureActors(): void {
  demoAccount = createQxreadAccount({
    accountId: DEMO_ACCOUNT_ID,
    displayName: 'Demo PWA User',
    roles: ['reader'],
  });
  demoSession = createQxreadSession({
    state: 'authenticated',
    accountId: DEMO_ACCOUNT_ID,
    issuedAt: DEMO_NOW,
  });
}

function project(): void {
  if (demoInstall === null) {
    demoProjection = null;
    return;
  }
  demoProjection = projectPwaInstallSurface({
    session: demoSession,
    account: demoAccount,
    install: demoInstall,
    preferences: demoPrefs,
    projectedAt: DEMO_NOW,
  });
}

export function resetDemoPwaStore(): void {
  ensureActors();
  demoInstall = null;
  demoPrefs = [];
  demoProjection = null;
  lastStatus = 'No PWA install record yet.';
}

export function getDemoPwaStatus(): string {
  return lastStatus;
}

export function getDemoPwaInstall(): QxreadPwaInstallRecord | null {
  return demoInstall;
}

export function getDemoPwaProjection(): QxreadPwaInstallProjection | null {
  return demoProjection;
}

export function createDemoPwaInstall(): string {
  ensureActors();
  const result = createPwaInstallRecord({
    session: demoSession,
    account: demoAccount,
    installState: 'promptable',
    installRecordId: 'qxrdpwa_demo',
    updatedAt: DEMO_NOW,
  });
  if (!result.allowed) {
    lastStatus = result.reason;
    return lastStatus;
  }
  demoInstall = result.install;
  project();
  lastStatus = `Promptable · unlocksContent=${String(demoInstall.unlocksContent)} · preparesOfflinePackage=${String(demoInstall.preparesOfflinePackage)}`;
  return lastStatus;
}

export function acceptDemoPwaInstall(): string {
  if (demoInstall === null) {
    lastStatus = 'Create a PWA install record first.';
    return lastStatus;
  }
  demoInstall = transitionPwaInstallState({
    install: demoInstall,
    nextState: 'installed',
    updatedAt: DEMO_NOW,
  });
  project();
  lastStatus = `Installed · unlocksContent=${String(demoInstall.unlocksContent)} · grantsEntitlement=${String(demoInstall.grantsEntitlement)}`;
  return lastStatus;
}

export function registerDemoPwaShellWorker(): string {
  if (demoInstall === null) {
    lastStatus = 'Create a PWA install record first.';
    return lastStatus;
  }
  demoInstall = registerPwaShellWorker({ install: demoInstall, updatedAt: DEMO_NOW });
  project();
  lastStatus = `SW registered · cachesPublicationTruth=${String(demoInstall.cachesPublicationTruth)} · grantsEntitlement=${String(demoInstall.grantsEntitlement)}`;
  return lastStatus;
}

export function enableDemoProductNotifications(): string {
  ensureActors();
  const result = upsertNotificationPreference({
    session: demoSession,
    account: demoAccount,
    channel: 'product',
    enabled: true,
    preferenceId: 'qxrdnpref_demo_product',
    updatedAt: DEMO_NOW,
  });
  if (!result.allowed) {
    lastStatus = result.reason;
    return lastStatus;
  }
  demoPrefs = [result.preference];
  project();
  lastStatus = `Preference product enabled · inventsEntitlement=${String(result.preference.inventsEntitlement)} · grantsAccess=${String(result.preference.grantsAccess)}`;
  return lastStatus;
}
